import React, { useState } from 'react';
import { Loader2, ShieldAlert, ShieldCheck } from 'lucide-react';
import SsnReviewDialog from '../SsnReviewDialog';

const day = (iso) => {
  const d = iso ? new Date(iso) : null;
  return d && !Number.isNaN(d.getTime()) ? d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) : '';
};

// Ticket 176: scans and photos held for an SSN mask review are kept by the
// server for a few days, so this list brings them back after a page refresh.
// A held file gets its review here; a reviewed one is read by Audrey from the
// stored copy, with no second drop. Hidden when nothing is waiting.
export function WaitingSsnReviews({ items, busy, onReviewed, onRead }) {
  if (!items || items.length === 0) return null;
  return (
    <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5" data-testid="ssn-waiting">
      <div className="flex items-center gap-2 mb-1 text-sm font-medium text-slate-500">
        <ShieldAlert size={14} className="text-amber-700" /> Waiting for SSN review ({items.length})
      </div>
      <p className="text-[12px] text-slate-500 mb-3">
        Audrey held these files until the Social Security number is masked. They stay here if you leave the page.
      </p>
      <ul className="divide-y divide-slate-100">
        {items.map((item) => <WaitingRow key={item.review_id} item={item} busy={busy} onReviewed={onReviewed} onRead={onRead} />)}
      </ul>
    </div>
  );
}

function WaitingRow({ item, busy, onReviewed, onRead }) {
  const [reviewing, setReviewing] = useState(false);
  const held = item.status === 'held';
  return (
    <li className="py-2.5 flex items-center gap-3" data-testid={`ssn-waiting-${item.review_id}`}>
      <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${held ? 'bg-amber-50 text-amber-700' : 'bg-emerald-50 text-emerald-700'}`}>
        {held ? <ShieldAlert size={14} /> : <ShieldCheck size={14} />}
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-[13px] font-semibold truncate text-brand-navy">{item.filename || 'Uploaded file'}</div>
        <div className="text-[11px] text-slate-500 flex items-center gap-x-2 flex-wrap">
          {item.uploader_name && <span>From {item.uploader_name}</span>}
          {day(item.created_at) && <span>Dropped {day(item.created_at)}</span>}
          {day(item.expires_at) && <span>Kept until {day(item.expires_at)}</span>}
        </div>
      </div>
      {held ? (
        <div className="shrink-0 flex items-center gap-2">
          <span className="inline-block text-[11px] font-semibold px-2 py-1 rounded-md bg-amber-50 text-amber-800 border border-amber-200">Needs SSN masking</span>
          <button type="button" onClick={() => setReviewing(true)}
            className="text-[11px] font-semibold px-2.5 py-1 rounded-md bg-brand-navy text-white"
            data-testid={`ssn-waiting-review-${item.review_id}`}>Review</button>
          <SsnReviewDialog reviewId={item.review_id} filename={item.filename} open={reviewing} onOpenChange={setReviewing} onReviewed={onReviewed} />
        </div>
      ) : (
        <div className="shrink-0 flex items-center gap-2">
          <span className="inline-block text-[11px] font-semibold px-2 py-1 rounded-md bg-emerald-50 text-emerald-800 border border-emerald-200">SSN masked</span>
          <button type="button" disabled={busy} onClick={() => onRead?.([item.review_id])}
            className="text-[11px] font-semibold px-2.5 py-1 rounded-md bg-brand-navy text-white disabled:opacity-50 inline-flex items-center gap-1.5"
            data-testid={`ssn-waiting-read-${item.review_id}`}>
            {busy && <Loader2 size={11} className="animate-spin" />} Read now
          </button>
        </div>
      )}
    </li>
  );
}
